import React from 'react'
import { Alert, Box, Chip, Grid, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { AnimatedSection, AnimatedTableRow } from '../../components/animations/MotionContainers'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { API_REQUESTS_PAUSED } from '../../data'
import { getIntegrations } from '../../services/admin/integrationsApi'
import { flattenIntegrationAlerts } from '../../utils/adminIntegrationData'
import { getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'

const buildMetrics = (alerts) => {
  const activeErrors = alerts.filter((alert) => alert.tone === 'error').length
  const warnings = alerts.filter((alert) => alert.tone === 'warning').length
  const pendingRetries = alerts.filter((alert) => !['-', '0', 0, null, undefined].includes(alert.retries)).length

  return [
    { title: 'Errores activos', value: String(activeErrors), icon: <ErrorOutlineRoundedIcon /> },
    { title: 'Advertencias', value: String(warnings), icon: <WarningAmberRoundedIcon /> },
    { title: 'Reintentos pendientes', value: String(pendingRetries), icon: <ReplayRoundedIcon /> },
    { title: 'Incidencias totales', value: String(alerts.length), icon: <WarningAmberRoundedIcon /> },
  ]
}

export const AdminProcessErrors = () => {
  const [alerts, setAlerts] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const metrics = React.useMemo(() => buildMetrics(alerts), [alerts])

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined

    let active = true

    const loadAlerts = async () => {
      setLoading(true)
      setErrorMessage('')

      try {
        const response = await getIntegrations({ page: 1, limit: 500, sortDirection: 'asc' })
        if (active) setAlerts(flattenIntegrationAlerts(getPaginatedItems(response)))
      } catch (error) {
        if (active) setErrorMessage(getApiErrorMessage(error, 'No se han podido cargar las incidencias.'))
      } finally {
        if (active) setLoading(false)
      }
    }

    loadAlerts()

    return () => {
      active = false
    }
  }, [])

  return (
    <Box className="page-fade">
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography variant="h4">Procesos con error</Typography>
          <Typography>Seguimiento de errores, advertencias y reintentos pendientes de las integraciones.</Typography>
        </Box>
      </Box>

      <Grid container spacing={2.1}>
        {metrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Typography className="section-title">Incidencias activas</Typography>
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}
        {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        <Box className="activity-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Proceso</TableCell>
                <TableCell>Integración</TableCell>
                <TableCell>Hora</TableCell>
                <TableCell>Severidad</TableCell>
                <TableCell>Mensaje</TableCell>
                <TableCell>Reintentos</TableCell>
                <TableCell>Responsable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow component={AnimatedTableRow} key={alert.id} hover>
                  <TableCell>{alert.process}</TableCell>
                  <TableCell className="admin-execution-type">{alert.integration}</TableCell>
                  <TableCell>{alert.time}</TableCell>
                  <TableCell>
                    <Chip size="small" className={`admin-status-chip admin-status-${alert.tone}`} label={alert.severity} />
                  </TableCell>
                  <TableCell>{alert.message}</TableCell>
                  <TableCell>{alert.retries}</TableCell>
                  <TableCell>{alert.owner}</TableCell>
                </TableRow>
              ))}
              {!loading && alerts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography className="admin-user-empty">No hay incidencias activas.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>
    </Box>
  )
}
